/**
 * DateRangePickerV2 Types
 *
 * Type definitions for the date range picker component with preset support.
 */

/** Categories for grouping date range presets */
export enum DateRangePresetCategory {
  Day = 'day',
  Week = 'week',
  Month = 'month',
  Quarter = 'quarter',
  Year = 'year',
}

/** All available date range presets */
export enum DateRangePreset {
  // Day presets
  Today = 'today',
  Yesterday = 'yesterday',
  Tomorrow = 'tomorrow',
  // Week presets
  ThisWeek = 'thisWeek',
  LastWeek = 'lastWeek',
  NextWeek = 'nextWeek',
  // Month presets
  ThisMonth = 'thisMonth',
  LastMonth = 'lastMonth',
  NextMonth = 'nextMonth',
  // Quarter presets
  ThisQuarter = 'thisQuarter',
  LastQuarter = 'lastQuarter',
  NextQuarter = 'nextQuarter',
  // Year presets
  ThisYear = 'thisYear',
  LastYear = 'lastYear',
  NextYear = 'nextYear',
}

/** Date range value with start and end dates */
export interface DateRangeV2 {
  startDate: Date;
  endDate: Date;
}

/** Hook input can be a preset string or actual dates */
export type DateRangeDefaultValue = DateRangePreset | DateRangeV2;

/** Props for the DateRangePickerV2 component */
export interface DateRangePickerV2Props {
  /** Current date range value. Pass null to show unselected state. */
  dates: DateRangeV2 | null;
  /** Callback when date range changes */
  onChange: (dates: DateRangeV2) => void;
  /** Currently selected preset (for display purposes) */
  selectedPreset?: DateRangePreset | null;
  /** Callback when a preset is selected */
  onPresetSelect?: (preset: DateRangePreset) => void;
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** Override reset behavior. When provided, called instead of the default reset-to-today. */
  onReset?: () => void;
  /** Whether the picker is disabled */
  disabled?: boolean;
  defaultPrefix?: string;
  /** Preferred side for the dropdown popover. Default: 'bottom'. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Preferred alignment of the dropdown popover. Default: 'end'. */
  align?: 'start' | 'center' | 'end';
  /** Pixel offset along the alignment axis. Default: 0. */
  alignOffset?: number;
  /** Pixel offset along the side axis. Default: 4. */
  sideOffset?: number;
  /** Padding from the viewport edge for collision detection. Default: 16. */
  collisionPadding?: number;
  /** Whether Radix should auto-flip to avoid viewport collisions. Default: true. */
  avoidCollisions?: boolean;
  /**
   * When true, the dropdown's collision boundary is the viewport (`[]`) instead
   * of the nearest scrolling ancestor. Use this when the picker sits inside a
   * horizontally clipped container — e.g. our generate-invoices header — so the
   * calendar isn't cut off at the container edge.
   */
  useViewportCollisionBoundary?: boolean;
  /** Custom class name for the trigger button. */
  triggerClassName?: string;
  /**
   * Preset categories to HIDE from the dropdown (e.g. Year/Quarter on the
   * generate-invoices/bills screens, which can't handle huge date ranges yet).
   * Omit to show every category. Custom calendar selection is unaffected.
   */
  hiddenPresetCategories?: DateRangePresetCategory[];
}

/** Options for useDateRangePickerV2 hook */
export interface UseDateRangePickerV2Options {
  /** Default value - can be a preset string or actual dates */
  defaultValue?: DateRangeDefaultValue | null;
}

/** Return type for useDateRangePickerV2 hook */
export interface UseDateRangePickerV2Return {
  /** Current date range. Null when nothing is selected. */
  dates: DateRangeV2 | null;
  /** Currently selected preset (null if custom range) */
  selectedPreset: DateRangePreset | null;
  /** Handler for date range changes (clears preset) */
  onChange: (dates: DateRangeV2) => void;
  /** Handler for preset selection */
  onPresetSelect: (preset: DateRangePreset) => void;
}

/** Props for PresetCategoryItem component */
export interface PresetCategoryItemProps {
  /** The category to display (day, week, month, etc.) */
  category: DateRangePresetCategory;
  /** Currently selected preset */
  selectedPreset: DateRangePreset | null;
  /** Callback when a preset is selected */
  onSelect: (preset: DateRangePreset) => void;
}

/** Configuration for a preset option in submenu */
export interface PresetOption {
  /** The preset identifier */
  preset: DateRangePreset;
  /** i18n translation key */
  labelKey: string;
}

/** Configuration for a category in the main menu */
export interface CategoryConfig {
  /** The category identifier */
  category: DateRangePresetCategory;
  /** i18n translation key for category label */
  labelKey: string;
  /** Available presets for this category */
  presets: PresetOption[];
}
